import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';

export const logOut = async () => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.AUTH.GET}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const resetPassword = async (data) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.AUTH.RESET_PASSWORD}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const forgotPassword = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.FORGOT_PASSWORD}`, data);
  } catch (error) {
    console.error(error, error?.message);
    throw error;
  }
};

/** Per-provider SSO endpoint constants, keyed by provider so callers stay generic. */
const SSO_PROVIDER_ENDPOINTS = {
  microsoft: {
    authorize: API.AUTH.SSO_MICROSOFT_AUTHORIZE,
    enabled: API.AUTH.SSO_MICROSOFT_ENABLED,
    connect: API.AUTH.SSO_MICROSOFT_CONNECT,
    directories: API.AUTH.SSO_MICROSOFT_DIRECTORIES,
  },
  google: {
    authorize: API.AUTH.SSO_GOOGLE_AUTHORIZE,
    enabled: API.AUTH.SSO_GOOGLE_ENABLED,
    connect: API.AUTH.SSO_GOOGLE_CONNECT,
    directories: API.AUTH.SSO_GOOGLE_DIRECTORIES,
  },
};

/**
 * Resolves the endpoint set for a provider, defaulting to Microsoft for safety.
 * @param {string} provider - 'microsoft' | 'google'.
 * @returns {{authorize: string, enabled: string, connect: string, directories: string}} the endpoints.
 */
const ssoEndpoints = (provider) => SSO_PROVIDER_ENDPOINTS[provider] || SSO_PROVIDER_ENDPOINTS.microsoft;

/**
 * Full-page URL the login button navigates to, to start a provider's login.
 * @param {string} provider - 'microsoft' | 'google'.
 * @returns {string} absolute authorize URL.
 */
export const ssoAuthorizeUrl = (provider) => `${apiEndpoint}${ssoEndpoints(provider).authorize}`;

/**
 * Checks whether a provider's SSO is enabled for the current tenant, so the login
 * page can decide whether to show that provider's button. Never throws.
 * @param {string} provider - 'microsoft' | 'google'.
 * @returns {Promise<boolean>} true when the button should be shown.
 */
export const isSsoProviderEnabled = async (provider) => {
  try {
    const response = await apiClient.get(`${apiEndpoint}${ssoEndpoints(provider).enabled}`);
    return Boolean(response?.data?.enabled);
  } catch (err) {
    return false;
  }
};

/**
 * Backward-compatible Microsoft wrapper around isSsoProviderEnabled.
 * @returns {Promise<boolean>} true when the Microsoft button should be shown.
 */
export const isMicrosoftSsoEnabled = async () => isSsoProviderEnabled('microsoft');

/**
 * Starts the admin directory-connect flow for a provider. Returns the provider URL
 * the browser should navigate to; the directory id is captured from the admin's
 * sign-in on the callback.
 * @param {string} provider - 'microsoft' | 'google'.
 * @returns {Promise<string|null>} the provider authorization URL, or null on failure.
 */
export const startSsoConnect = async (provider) => {
  try {
    const response = await apiClient.post(`${apiEndpoint}${ssoEndpoints(provider).connect}`, {});
    return response?.data?.redirectUrl || null;
  } catch (err) {
    return null;
  }
};

/**
 * Lists the directories connected for a provider for the current company. Admin-only
 * on the server. Never throws to the caller.
 * @param {string} provider - 'microsoft' | 'google'.
 * @returns {Promise<Array<object>|null>} connected directories (possibly empty), or null on failure
 *   so the caller can distinguish an outage from a genuinely empty list.
 */
export const listSsoDirectories = async (provider) => {
  try {
    const response = await apiClient.get(`${apiEndpoint}${ssoEndpoints(provider).directories}`);
    return response?.data?.directories || [];
  } catch (err) {
    return null;
  }
};

/**
 * Enables or disables just-in-time provisioning for one connected directory.
 * @param {string} provider - 'microsoft' | 'google'.
 * @param {string} directoryId - the directory mapping id.
 * @param {boolean} allowJitProvisioning - the new JIT setting.
 * @returns {Promise<object|null>} the updated directory, or null on failure.
 */
export const setSsoDirectoryJit = async (provider, directoryId, allowJitProvisioning) => {
  try {
    const response = await apiClient.patch(`${apiEndpoint}${ssoEndpoints(provider).directories}/${directoryId}`, { allowJitProvisioning });
    return response?.data?.directory || null;
  } catch (err) {
    return null;
  }
};

/**
 * Soft-disconnects a connected directory (kill switch off). Existing accounts are
 * preserved server-side. Reports whether the admin's own SSO session was revoked by
 * this action so the caller can sign them out cleanly.
 * @param {string} provider - 'microsoft' | 'google'.
 * @param {string} directoryId - the directory mapping id.
 * @returns {Promise<{disconnected: boolean, selfSignedOut: boolean}>} the outcome.
 */
export const disconnectSsoDirectory = async (provider, directoryId) => {
  try {
    const response = await apiClient.delete(`${apiEndpoint}${ssoEndpoints(provider).directories}/${directoryId}`);
    return {
      disconnected: Boolean(response?.data?.disconnected),
      selfSignedOut: Boolean(response?.data?.selfSignedOut),
    };
  } catch (err) {
    return { disconnected: false, selfSignedOut: false };
  }
};

/** Backward-compatible Microsoft wrappers around the provider-generic functions. */
export const startMicrosoftSsoConnect = async () => startSsoConnect('microsoft');
export const listMicrosoftSsoDirectories = async () => listSsoDirectories('microsoft');
export const setMicrosoftSsoDirectoryJit = async (directoryId, allowJitProvisioning) => setSsoDirectoryJit('microsoft', directoryId, allowJitProvisioning);
export const disconnectMicrosoftSsoDirectory = async (directoryId) => disconnectSsoDirectory('microsoft', directoryId);

/**
 * Reads the company's user-seat usage against the plan limit for the SSO page.
 * Never throws to the caller.
 * @returns {Promise<{used: number, limit: (number|null), unlimited: boolean}|null>} usage, or null on failure.
 */
export const getSsoSeatUsage = async () => {
  try {
    const response = await apiClient.get(`${apiEndpoint}${API.AUTH.SSO_SEAT_USAGE}`);
    return response?.data || null;
  } catch (err) {
    return null;
  }
};

/**
 * Redeems a one-time SSO login ticket for a session. Returns the same envelope
 * as the password login endpoint.
 * @param {{ticket: string}} data - the opaque ticket from the SSO callback redirect.
 * @returns {Promise<object>} api response envelope.
 */
export const ssoTicketLogin = async (data) =>
  apiClient.post(`${apiEndpoint}${API.AUTH.SSO_TICKET}`, data, { skipSessionExpiryHandling: true });

/** Key the short-lived onboarding token is parked under between setup screens. */
const SETUP_TOKEN_KEY = 'ssoSetupToken';

/**
 * Stores the onboarding token handed back by set-password or by a login that still
 * needs 2FA/security-question setup. Session-scoped: it must not outlive the tab.
 * @param {string} token - plaintext setup token.
 */
export const storeSetupToken = (token) => {
  if (token) {
    sessionStorage.setItem(SETUP_TOKEN_KEY, token);
  }
};

/** Clears the onboarding token once setup finishes. */
export const clearSetupToken = () => {
  sessionStorage.removeItem(SETUP_TOKEN_KEY);
};

/**
 * Whether an onboarding token is currently held. The setup screens use this to
 * detect that the user arrived without one — e.g. they set a password, closed the
 * tab before finishing 2FA, and came back to a now-consumed invite link — and can
 * then send them to sign in, which mints a fresh token.
 * @returns {boolean} true when a token is present.
 */
export const hasSetupToken = () => Boolean(sessionStorage.getItem(SETUP_TOKEN_KEY));

/**
 * Header carrying the onboarding token. The setup endpoints identify the account
 * from this token, never from an email in the URL.
 * @returns {object} headers object, empty when no token is held.
 */
const setupTokenHeader = () => {
  const token = sessionStorage.getItem(SETUP_TOKEN_KEY);
  return token ? { 'x-setup-token': token } : {};
};

export const setSecurityQuestions = async (data) => {
  try {
    const queryString = data.email && data.email !== 'null' ? `email=${data.email}` : `userId=${data.userId}`;
    return await apiClient.post(
      `${apiEndpoint}${API.AUTH.SET_SECURITY_QUESTIONS}?${queryString}`,
      { questions: data.questions },
      { headers: setupTokenHeader() },
    );
  } catch (error) {
    console.error(error, error?.message);
    throw error;
  }
};
export const login = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.LOGIN}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchCompanyRights = async () => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.USER_RIGHTS.GET_COMPANY_RIGHTS}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const signup = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.SIGN_UP}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchSecurityQuestion = async (data) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.AUTH.SET_SECURITY_QUESTIONS}`, {
      params: { ...data },
      headers: setupTokenHeader(),
    });
  } catch (error) {
    console.error(error, error?.message);
    throw error;
  }
};

export const verifySecurityQuestion = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.VERIFY_SECURITY_QUESTIONS}`, data);
  } catch (error) {
    console.error(error, error?.message);
    throw error;
  }
};

export const resetUserPassword = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.RESET_USER_PASSWORD}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const verifyOtp = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.VERIFY_OTP}`, data, { headers: setupTokenHeader() });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getMfaQrCodeData = async (data) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.AUTH.GET_MFA_QR_CODE}`, { headers: setupTokenHeader() });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const enableMfaSetup = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.ENABLE_MFA}`, data, { headers: setupTokenHeader() });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const verifiedCode = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.VERIFY_CODE}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const recoverMFAaccount = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.RECOVER_MFA_ACCOUNT}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const recoveryRequest = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.AUTH.RECOVERY_REQUEST}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const changeSecurityQuestions = async (data) => {
  try {
    const queryString = data.email ? `email=${data.email}` : `userId=${data.userId}`;
    return await apiClient.put(
      `${apiEndpoint}${API.AUTH.SET_SECURITY_QUESTIONS}?${queryString}`,
      { questions: data.questions },
      { headers: setupTokenHeader() },
    );
  } catch (error) {
    console.error(error, error?.message);
    throw error;
  }
};

export const getIsVerified = async (id) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.AUTH.USER_VERIFIED}?userId=${id}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getUserverify = async (data) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.AUTH.USER_VERIFY}?code=${data.code}&companyAdminEmail=${data.companyAdminEmail}&id=${data.id}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
